import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { DashboardSkeleton, ListRowsSkeleton } from '../components/PageSkeletons'
import { Link, useNavigate, useSearchParams, Navigate } from 'react-router-dom'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { dashboardApi, requestsApi } from '../services/api'
import { useTheme } from '../context/ThemeContext'
import { getChartTheme } from '../utils/chartTheme'
import { formatDate, formatTime, formatTimeAgo as formatTimeAgoUtil, parseUTC } from '../utils/dateUtils'

import {
  FileText,
  Plus,
  Upload,
  Download,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  TrendingUp,
  Eye,
  Edit3,
  Filter,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  Check,
  ArrowUpRight,
  BarChart3,
  Activity,
  Database,
  Cpu,
  ChevronDown,
  CheckSquare,
  Square,
  Maximize2
} from 'lucide-react'
import DatePicker from '../components/DatePicker'
import { AllOpCosChip } from '../components/opco/OpCoBadge'

function Dashboard() {
  const DEFAULT_STATS = {
    requests: {
      total: 0,
      pending: 0,
      processing: 0,
      reviewing: 0,
      completed: 0,
      cancelled: 0,
    },
  }

  const hasLoadedRef = useRef(false)
  const [stats, setStats] = useState(null)
  const [requests, setRequests] = useState([])
  const [recentActivity, setRecentActivity] = useState([])
  const [systemHealth, setSystemHealth] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(new Date())
  const [exporting, setExporting] = useState(false)

  
  // Global date filter (drives stats cards + export)
  const [globalDateFrom, setGlobalDateFrom] = useState('')
  const [globalDateTo, setGlobalDateTo] = useState('')
  // Local input state for date pickers (committed on Apply)
  const [inputDateFrom, setInputDateFrom] = useState('')
  const [inputDateTo, setInputDateTo] = useState('')
  const hasUnappliedDateChanges = inputDateFrom !== globalDateFrom || inputDateTo !== globalDateTo

  // Table filter states
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [createdByFilter, setCreatedByFilter] = useState('')
  const [tableSearch, setTableSearch] = useState('')
  const [appliedFilters, setAppliedFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: 'all',
    createdBy: '',
    search: '',
  })
  const hasUnappliedTableFilterChanges =
    dateFrom !== appliedFilters.dateFrom ||
    dateTo !== appliedFilters.dateTo ||
    statusFilter !== appliedFilters.status ||
    createdByFilter !== appliedFilters.createdBy ||
    tableSearch !== appliedFilters.search
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [totalResults, setTotalResults] = useState(0)

  // Sorting
  const [sortField, setSortField] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }
  
  const requestParams = useMemo(() => ({
    page: currentPage,
    per_page: perPage,
    status: appliedFilters.status !== 'all' ? appliedFilters.status : undefined,
    created_by: appliedFilters.createdBy || undefined,
    search: appliedFilters.search || undefined,
    date_from: appliedFilters.dateFrom || undefined,
    date_to: appliedFilters.dateTo || undefined,
  }), [currentPage, perPage, appliedFilters])

  const fetchOverviewData = useCallback(async () => {
    const [statsRes, recentRes] = await Promise.allSettled([
      dashboardApi.getStats({
        date_from: globalDateFrom || undefined,
        date_to: globalDateTo || undefined
      }),
      dashboardApi.getActivity(6),
    ])

    const statsLoaded = statsRes.status === 'fulfilled'
    const activityLoaded = recentRes.status === 'fulfilled'

    if (statsLoaded) {
      setStats(statsRes.value.data)
    } else {
      console.warn('Dashboard stats fetch failed:', statsRes.reason)
      setStats((prev) => prev || DEFAULT_STATS)
    }

    if (activityLoaded) {
      setRecentActivity(recentRes.value.data.activity || [])
    } else {
      console.warn('Dashboard activity fetch failed:', recentRes.reason)
      setRecentActivity((prev) => prev || [])
    }

    if (!statsLoaded && !activityLoaded) {
      throw new Error('Failed to load dashboard overview')
    }

    return { statsLoaded, activityLoaded }
  }, [globalDateFrom, globalDateTo])

  const fetchRequestsData = useCallback(async () => {
    const requestsRes = await requestsApi.list(requestParams)
    setRequests(requestsRes.data.requests || [])
    setTotalResults(requestsRes.data.pagination?.total || 0)
  }, [requestParams])

  const runDataLoad = useCallback(async ({ showLoading = false, silent = false } = {}) => {
    try {
      if (showLoading) setLoading(true)
      const [overviewRes, requestsRes] = await Promise.allSettled([
        fetchOverviewData(),
        fetchRequestsData(),
      ])

      const overviewOk = overviewRes.status === 'fulfilled'
      const requestsOk = requestsRes.status === 'fulfilled'

      if (overviewOk || requestsOk) {
        setLastUpdated(new Date())
      }

      if (!silent) {
        if (!overviewOk && !requestsOk) {
          setError('Failed to load dashboard data')
        } else if (!requestsOk) {
          setError('Failed to load recent documents')
        } else {
          setError(null)
        }
      }

      if (!overviewOk) {
        console.error('Overview load failed:', overviewRes.reason)
      }
      if (!requestsOk) {
        console.error('Requests load failed:', requestsRes.reason)
      }
    } catch (err) {
      const logPrefix = silent ? 'Background refresh failed:' : 'Error fetching dashboard data:'
      console.error(logPrefix, err)
      if (!silent) setError('Failed to load dashboard data')
    } finally {
      if (showLoading) setLoading(false)
    }
  }, [fetchOverviewData, fetchRequestsData])

  // Two separate effects on purpose. runDataLoad depends on fetchRequestsData,
  // which depends on requestParams — so driving both from one effect meant that
  // turning a page in the request table also refetched the stat cards and the
  // activity feed, neither of which is affected by pagination. That was two
  // extra API round trips per page click.
  useEffect(() => {
    const shouldShowLoading = !hasLoadedRef.current
    if (shouldShowLoading) setLoading(true)
    Promise.all([fetchOverviewData(), fetchRequestsData()])
      .then(() => {
        setLastUpdated(new Date())
        setError(null)
      })
      .catch(err => {
        console.error('Error fetching dashboard data:', err)
        setError('Failed to load dashboard data')
      })
      .finally(() => {
        hasLoadedRef.current = true
        if (shouldShowLoading) setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Overview reacts only to the global date range.
  useEffect(() => {
    if (!hasLoadedRef.current) return
    fetchOverviewData()
      .then(() => setLastUpdated(new Date()))
      .catch(err => console.error('Error refreshing overview:', err))
  }, [fetchOverviewData])

  // The request table reacts only to its own params (page, per-page, filters).
  useEffect(() => {
    if (!hasLoadedRef.current) return
    fetchRequestsData()
      .then(() => setLastUpdated(new Date()))
      .catch(err => console.error('Error refreshing requests:', err))
  }, [fetchRequestsData])
  
  // Silent auto-refresh every 60 seconds - no loading spinners, no UI disruption
  useEffect(() => {
    const interval = setInterval(() => {
      runDataLoad({ silent: true })
    }, 60000)
    return () => clearInterval(interval)
  }, [runDataLoad])

  // System health: fetch once on mount, then every 5 minutes
  useEffect(() => {
    const fetchHealth = () => {
      dashboardApi.getSystemHealth()
        .then(res => setSystemHealth(res.data))
        .catch(err => console.error('Health check failed:', err))
    }
    fetchHealth()
    const healthInterval = setInterval(fetchHealth, 300000)
    return () => clearInterval(healthInterval)
  }, [])
  
  const handleApplyFilters = () => {
    setCurrentPage(1)
    setAppliedFilters({
      dateFrom,
      dateTo,
      status: statusFilter,
      createdBy: createdByFilter,
      search: tableSearch,
    })
  }
  
  const handleResetFilters = () => {
    setDateFrom('')
    setDateTo('')
    setStatusFilter('all')
    setCreatedByFilter('')
    setTableSearch('')
    setCurrentPage(1)
    setAppliedFilters({
      dateFrom: '',
      dateTo: '',
      status: 'all',
      createdBy: '',
      search: '',
    })
  }

  const handleClearGlobalDateFilter = () => {
    setInputDateFrom('')
    setInputDateTo('')
    setGlobalDateFrom('')
    setGlobalDateTo('')
  }

  const handleApplyGlobalDateFilter = () => {
    setGlobalDateFrom(inputDateFrom)
    setGlobalDateTo(inputDateTo)
  }

  const handleExportReport = async () => {
    try {
      setExporting(true)
      const params = {}
      if (globalDateFrom) params.date_from = globalDateFrom
      if (globalDateTo) params.date_to = globalDateTo

      const response = await dashboardApi.exportReport(params)
      
      // Create a download link from the blob
      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition']
      let filename = `monthly_report_${new Date().toISOString().slice(0, 10)}.csv`
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/)
        if (match) filename = match[1]
      }
      
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Error exporting report:', err)
      setError('Failed to export report. Please try again.')
    } finally {
      setExporting(false)
    }
  }

  // Map activity type to icon/color matching the original design
  const getActivityMeta = (type) => {
    switch (type) {
      case 'publish':
        return { icon: <Check className="h-4 w-4 text-green-600 dark:text-green-400" />, iconBg: 'bg-green-100 dark:bg-green-900/30' }
      case 'cancel':
        return { icon: <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />, iconBg: 'bg-red-100 dark:bg-red-900/30' }
      case 'upload':
        return { icon: <Upload className="h-4 w-4 text-blue-600 dark:text-blue-400" />, iconBg: 'bg-blue-100 dark:bg-blue-900/30' }
      case 'extraction':
        return { icon: <Cpu className="h-4 w-4 text-purple-600 dark:text-purple-400" />, iconBg: 'bg-purple-100 dark:bg-purple-900/30' }
      case 'edit':
        return { icon: <Edit3 className="h-4 w-4 text-orange-600 dark:text-orange-400" />, iconBg: 'bg-orange-100 dark:bg-orange-900/30' }
      case 'new_request':
        return { icon: <Plus className="h-4 w-4 text-navy-600 dark:text-navy-400" />, iconBg: 'bg-navy-100 dark:bg-navy-900/30' }
      default:
        return { icon: <Activity className="h-4 w-4 text-gray-600 dark:text-gray-400" />, iconBg: 'bg-gray-100 dark:bg-gray-700' }
    }
  }

  // Human-readable relative time (UK timezone-aware)
  const formatTimeAgo = formatTimeAgoUtil

  if (loading && !stats) {
    return (
      <DashboardSkeleton />
    )
  }
  
  if (error && !stats) {
    return (
      <div className="max-w-md mx-auto mt-12">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-red-100 dark:bg-red-900/40 rounded-full">
              <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-red-800 dark:text-red-300">Failed to load dashboard</h3>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>
              <button
                onClick={() => runDataLoad({ showLoading: true })}
                disabled={loading}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Calculate stats from API data
  const totalDocuments = stats?.requests?.total || 0
  const pendingReviews = stats?.requests?.reviewing || 0
  const completedRequests = stats?.requests?.completed || 0
  const cancelledRequests = stats?.requests?.cancelled || 0
  
  // Calculate status distribution for donut chart
  const statusData = {
    completed: stats?.requests?.completed || 0,
    reviewing: stats?.requests?.reviewing || 0,
    processing: stats?.requests?.processing || 0,
    pending: stats?.requests?.pending || 0,
    cancelled: stats?.requests?.cancelled || 0,
  }
  
  const totalForChart = Object.values(statusData).reduce((a, b) => a + b, 0)
  
  // Calculate pages
  const totalPages = Math.ceil(totalResults / perPage)
  
  // Use requests directly - filtering happens via API on Apply
  const filteredRequests = [...requests].sort((a, b) => {
    if (!sortField) return 0
    let aVal, bVal
    switch (sortField) {
      case 'name':
        aVal = (a.title || a.name || '').toLowerCase()
        bVal = (b.title || b.name || '').toLowerCase()
        break
      case 'processed':
        aVal = (parseUTC(a.updated_at || a.created_at) || new Date(0)).getTime()
        bVal = (parseUTC(b.updated_at || b.created_at) || new Date(0)).getTime()
        break
      case 'status':
        aVal = (a.status || '').toLowerCase()
        bVal = (b.status || '').toLowerCase()
        break
      case 'confidence':
        aVal = a.ai_confidence || a.confidence || 0
        bVal = b.ai_confidence || b.confidence || 0
        break
      default:
        return 0
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
    return 0
  })

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dashboard<AllOpCosChip /></h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Overview of processed documents and system performance</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Clock className="h-4 w-4" />
            Last updated: {lastUpdated.toLocaleTimeString() === new Date().toLocaleTimeString() ? 'Just now' : lastUpdated.toLocaleTimeString()}
          </div>
          <button
            onClick={() => runDataLoad({ showLoading: true })}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            aria-label="Refresh dashboard"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>
      
      {/* Export & Date Filter */}
      <div className="flex flex-wrap items-center gap-3 justify-end">
        <button 
          onClick={handleExportReport}
          disabled={exporting}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {exporting ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          {exporting ? 'Exporting...' : 'Export Report'}
        </button>

        {/* Separator */}
        <div className="h-8 w-px bg-gray-200 dark:bg-gray-700 mx-1" />

        {/* Date Filter */}
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          <DatePicker value={inputDateFrom} onChange={setInputDateFrom} max={inputDateTo || undefined} size="sm" placeholder="From" ariaLabel="From date" />
          <span className="text-gray-400 dark:text-gray-500 text-sm">to</span>
          <DatePicker value={inputDateTo} onChange={setInputDateTo} min={inputDateFrom || undefined} size="sm" placeholder="To" ariaLabel="To date" />
          <button
            onClick={handleApplyGlobalDateFilter}
            disabled={!hasUnappliedDateChanges}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-navy-800 text-white text-sm font-medium rounded-lg hover:bg-navy-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Apply
          </button>
          {(globalDateFrom || globalDateTo) && (
            <button
              onClick={handleClearGlobalDateFilter}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              title="Clear date filter"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {(globalDateFrom || globalDateTo) && (
          <span className="text-xs text-navy-600 dark:text-navy-300 bg-navy-50 dark:bg-navy-900/30 px-2.5 py-1 rounded-full font-medium">
            Filtered: {globalDateFrom || 'Start'} → {globalDateTo || 'Now'}
          </span>
        )}
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Documents */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Documents</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 stat-number-animate" key={`total-${totalDocuments}`}>{totalDocuments}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500">{globalDateFrom || globalDateTo ? 'Filtered period' : 'All time'}</p>
            </div>
          </div>
        </div>
        
        {/* Pending Reviews */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-50 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <Clock className="h-5 w-5 text-orange-500 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Pending Reviews</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 stat-number-animate" key={`pending-${pendingReviews}`}>{pendingReviews}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500">{globalDateFrom || globalDateTo ? 'Filtered period' : 'All time'}</p>
            </div>
          </div>
        </div>
        
        {/* Completed */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Completed</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 stat-number-animate" key={`completed-${completedRequests}`}>{completedRequests}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500">{globalDateFrom || globalDateTo ? 'Filtered period' : 'All time'}</p>
            </div>
          </div>
        </div>
        
        {/* Cancelled */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Cancelled</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 stat-number-animate" key={`cancelled-${cancelledRequests}`}>{cancelledRequests}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500">{globalDateFrom || globalDateTo ? 'Filtered period' : 'All time'}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Processing Trend */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <ProcessingTrendChart />
        </div>
        
        {/* Status Distribution */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Status Distribution</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{globalDateFrom || globalDateTo ? 'Filtered period' : 'All time'} document status</p>
            </div>
            <Link to="/requests" className="text-sm font-medium text-navy-600 dark:text-navy-400 hover:text-navy-800 dark:hover:text-navy-300">
              View All
            </Link>
          </div>
          <div className="flex-1 flex items-center">
            <StatusDonutChart data={statusData} total={totalForChart} />
          </div>
        </div>
      </div>
      
      {/* Recently Processed Documents Table with Filters */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <div className="mb-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Recently Processed Documents</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Latest document processing activity</p>
          </div>
          
          {/* Filters Row */}
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1.5">Search</label>
              <input
                type="text"
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                placeholder="Title, description, documents..."
                className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500 min-w-[220px] dark:bg-gray-800 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1.5">Date Range</label>
              <div className="flex items-center gap-2">
                <DatePicker value={dateFrom} onChange={setDateFrom} max={dateTo || undefined} placeholder="From" ariaLabel="From date" />
                <span className="text-gray-400 dark:text-gray-500 text-sm">to</span>
                <DatePicker value={dateTo} onChange={setDateTo} min={dateFrom || undefined} placeholder="To" ariaLabel="To date" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1.5">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500 min-w-[150px] dark:bg-gray-800 dark:text-gray-200"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="extracted">Extracted</option>
                <option value="reviewing">Reviewing</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="failed">Failed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1.5">Created By</label>
              <input
                type="text"
                value={createdByFilter}
                onChange={(e) => setCreatedByFilter(e.target.value)}
                placeholder="Name or email"
                className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500 min-w-[180px] dark:bg-gray-800 dark:text-gray-200"
              />
            </div>

            <button
              onClick={handleApplyFilters}
              disabled={!hasUnappliedTableFilterChanges}
              className="inline-flex items-center gap-2 px-6 py-2 bg-navy-800 text-white text-sm font-medium rounded-lg hover:bg-navy-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Filter className="h-4 w-4" />
              Apply
            </button>
            <button
              onClick={handleResetFilters}
              className="p-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <RefreshCw className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button onClick={() => handleSort('name')} className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                    Document Name
                    <SortIcon active={sortField === 'name'} direction={sortField === 'name' ? sortDirection : null} />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button onClick={() => handleSort('processed')} className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                    Processed Date
                    <SortIcon active={sortField === 'processed'} direction={sortField === 'processed' ? sortDirection : null} />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button onClick={() => handleSort('status')} className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                    Status
                    <SortIcon active={sortField === 'status'} direction={sortField === 'status' ? sortDirection : null} />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button onClick={() => handleSort('confidence')} className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                    AI Confidence
                    <SortIcon active={sortField === 'confidence'} direction={sortField === 'confidence' ? sortDirection : null} />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {filteredRequests.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <FileText className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
                      <p className="text-gray-500 dark:text-gray-400 font-medium">No documents found</p>
                      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Try adjusting your filters or add new documents</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredRequests.map((req, index) => (
                  <DocumentRow key={req.id} request={req} index={index} />
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <span>
              {totalResults === 0
                ? 'No results'
                : `Showing ${(currentPage - 1) * perPage + 1}\u2013${Math.min(currentPage * perPage, totalResults)} of ${totalResults} result${totalResults !== 1 ? 's' : ''}`}
            </span>
            <span className="border-l border-gray-200 dark:border-gray-700 h-4" />
            <span className="flex items-center gap-1.5">
              Rows per page
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value))
                  setCurrentPage(1)
                }}
                className="px-2 py-1 border border-gray-200 dark:border-gray-700 rounded text-sm dark:bg-gray-800 dark:text-gray-200"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            {generatePageNumbers(currentPage, totalPages).map((page, i) => (
              <React.Fragment key={i}>
                {page === '...' ? (
                  <span className="px-2 text-gray-400 dark:text-gray-500">...</span>
                ) : (
                  <button
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded text-sm font-medium ${
                      currentPage === page
                        ? 'bg-navy-800 text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    {page}
                  </button>
                )}
              </React.Fragment>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Bottom Section - 3 Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Recent Activity</h2>
            <Link to="/activity" className="text-sm font-medium text-navy-600 dark:text-navy-400 hover:text-navy-800 dark:hover:text-navy-300">
              View All
            </Link>
          </div>
          <div className="divide-y divide-gray-50 dark:divide-gray-800">
            {recentActivity.length > 0 ? (
              recentActivity.map((item, idx) => {
                const { icon, iconBg } = getActivityMeta(item.type)
                return (
                  <ActivityItem
                    key={`${item.type}-${item.entity_id}-${idx}`}
                    icon={icon}
                    iconBg={iconBg}
                    title={item.title}
                    time={formatTimeAgo(item.time)}
                  />
                )
              })
            ) : (
              <div className="px-5 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                No recent activity
              </div>
            )}
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Quick Actions</h2>
          </div>
          <div className="p-4 space-y-2">
            <Link to="/requests" className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group">
              <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-navy-600 dark:group-hover:text-navy-400">View All Requests</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Browse and manage documents</p>
              </div>
              <ArrowUpRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-navy-600 dark:group-hover:text-navy-400" />
            </Link>
            <Link to="/emails" className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group">
              <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center">
                <Upload className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-navy-600 dark:group-hover:text-navy-400">Email Queue</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Review incoming emails</p>
              </div>
              <ArrowUpRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-navy-600 dark:group-hover:text-navy-400" />
            </Link>
            <Link to="/templates" className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group">
              <div className="w-8 h-8 rounded-lg bg-green-50 dark:bg-green-900/30 flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-navy-600 dark:group-hover:text-navy-400">Templates</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Manage extraction templates</p>
              </div>
              <ArrowUpRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-navy-600 dark:group-hover:text-navy-400" />
            </Link>
            <Link to="/activity" className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group">
              <div className="w-8 h-8 rounded-lg bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center">
                <Activity className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-navy-600 dark:group-hover:text-navy-400">Activity Log</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Full audit trail</p>
              </div>
              <ArrowUpRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-navy-600 dark:group-hover:text-navy-400" />
            </Link>
            <Link to="/documents" className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center">
                <Database className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-navy-600 dark:group-hover:text-navy-400">Documents</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">View all extracted documents</p>
              </div>
              <ArrowUpRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-navy-600 dark:group-hover:text-navy-400" />
            </Link>
            <Link to="/settings" className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group">
              <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <SlidersHorizontal className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-navy-600 dark:group-hover:text-navy-400">Settings</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Configure organisation preferences</p>
              </div>
              <ArrowUpRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-navy-600 dark:group-hover:text-navy-400" />
            </Link>
          </div>
        </div>
        
        {/* System Health */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">System Health</h2>
            {systemHealth && (
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                systemHealth.overall === 'healthy' ? 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/30' :
                systemHealth.overall === 'degraded' ? 'text-orange-700 bg-orange-100 dark:text-orange-300 dark:bg-orange-900/30' :
                'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-900/30'
              }`}>
                {systemHealth.overall === 'healthy' ? 'Healthy' :
                 systemHealth.overall === 'degraded' ? 'Degraded' : 'Unhealthy'}
              </span>
            )}
          </div>
          <div className="p-4 space-y-4">
            {systemHealth ? (
              <>
                <HealthMetric
                  label="Database Latency"
                  value={systemHealth.database?.latency_ms >= 0 ? `${systemHealth.database.latency_ms}ms` : 'N/A'}
                  percentage={systemHealth.database?.latency_ms >= 0 ? Math.max(0, 100 - systemHealth.database.latency_ms / 5) : 0}
                  color={systemHealth.database?.latency_ms < 100 ? 'green' : systemHealth.database?.latency_ms < 500 ? 'orange' : 'red'}
                />
                <HealthMetric
                  label="Azure Storage"
                  value={systemHealth.storage?.status === 'connected' ? 'Connected' :
                         systemHealth.storage?.status === 'not_configured' ? 'Not Configured' : 'Disconnected'}
                  percentage={systemHealth.storage?.status === 'connected' ? 100 : 0}
                  color={systemHealth.storage?.status === 'connected' ? 'green' :
                         systemHealth.storage?.status === 'not_configured' ? 'orange' : 'red'}
                  showBar={false}
                />
                <HealthMetric
                  label="AI Service"
                  value={systemHealth.ai_service?.status === 'configured' ? 'Configured' :
                         systemHealth.ai_service?.status === 'not_configured' ? 'Not Configured' : 'Unknown'}
                  percentage={systemHealth.ai_service?.status === 'configured' ? 100 : 0}
                  color={systemHealth.ai_service?.status === 'configured' ? 'green' : 'orange'}
                  showBar={false}
                />
                <HealthMetric
                  label="Queue Processing"
                  value={systemHealth.queue?.status === 'normal' ? 'Normal' :
                         systemHealth.queue?.status === 'busy' ? `Busy (${systemHealth.queue.pending} pending)` :
                         systemHealth.queue?.status === 'degraded' ? `Degraded (${systemHealth.queue.failed} failed)` : 'Unknown'}
                  percentage={systemHealth.queue?.status === 'normal' ? 100 : systemHealth.queue?.status === 'busy' ? 60 : 30}
                  color={systemHealth.queue?.status === 'normal' ? 'green' :
                         systemHealth.queue?.status === 'busy' ? 'orange' : 'red'}
                  showBar={false}
                />
                <HealthMetric
                  label="Connection Pool"
                  value={`${systemHealth.pool?.in_use || 0} / ${systemHealth.pool?.size || 0}`}
                  percentage={systemHealth.pool?.size ? Math.round((systemHealth.pool.in_use / systemHealth.pool.size) * 100) : 0}
                  color={systemHealth.pool?.size && (systemHealth.pool.in_use / systemHealth.pool.size) > 0.8 ? 'orange' : 'green'}
                />
              </>
            ) : (
              <div className="py-4 text-center text-sm text-gray-400 dark:text-gray-500">Loading health data...</div>
            )}
          </div>
        </div>
      </div>


    </div>
  )
}

// Sort Icon Component
function SortIcon({ active, direction }) {
  return (
    <svg className={`h-3 w-3 transition-colors ${active ? 'text-navy-600 dark:text-navy-400' : 'text-gray-400'}`} viewBox="0 0 10 14" fill="currentColor">
      <path d="M5 0L9.33 5H0.67L5 0Z" className={active && direction === 'asc' ? 'opacity-100' : 'opacity-30'} />
      <path d="M5 14L0.67 9H9.33L5 14Z" className={active && direction === 'desc' ? 'opacity-100' : 'opacity-30'} />
    </svg>
  )
}

// Processing Trend Chart Component - stacked area chart by template
// ─────────────────────────────────────────────────────────────────
// Fixes applied:
//  • No series cap        → default view renders top MAX_CHART_SERIES by total + "Other(N)"
//                           bucket; when user picks specific templates from the dropdown
//                           the exact selection is shown regardless of rank
//  • template_id missing  → stored in seriesConfig so click-to-drill navigation works
//  • Color cycling        → extended palette to 16 distinct colors; "Other" is always gray
//  • Duplicate names      → first occurrence keeps the raw name; subsequent get " (2)", " (3)"
//  • Legend 3-row overflow → capped at MAX_CHART_SERIES series + Other keeps legend in 1 row
//  • Tooltip noise        → zero-value entries filtered out (see TrendTooltip)
// ─────────────────────────────────────────────────────────────────

// The 16 perceptually distinct series colours (so the first 8 never repeat;
// index 8+ wraps, which only triggers when a user hand-picks >8 templates) now
// live in utils/chartTheme.js, which returns a lighter, less-saturated variant
// for dark mode - saturated colours optically vibrate against dark grounds.

// How many individual series to show before collapsing the rest into "Other(N)".
// Keeps the legend to 1 row (~900px wide ÷ ~120px per item = room for ~7 + Other).
const MAX_CHART_SERIES = 8

// Build a guaranteed-unique chart key per series.
// Stores template_id so click-to-drill navigation can filter by template.
// Disambiguates duplicate display names ("Unknown (Auto)" → "Unknown (Auto) (2)").
function buildSeriesConfig(series, palette) {
  const seen = new Set()
  // First pass: count how many times each name appears so we know which need disambiguation
  const nameCount = {}
  series.forEach(s => { nameCount[s.template_name] = (nameCount[s.template_name] || 0) + 1 })
  const nameSeq = {}

  return series.map((s, i) => {
    // Unique stable key - never use template_name as a key (names can collide)
    let key = s.template_id != null ? `t${s.template_id}` : `no_tmpl_${i}`
    while (seen.has(key)) key = `${key}_${i}`
    seen.add(key)

    // Disambiguate: first occurrence keeps raw name; duplicates get " (2)", " (3)" …
    nameSeq[s.template_name] = (nameSeq[s.template_name] || 0) + 1
    const displayName = nameCount[s.template_name] > 1 && nameSeq[s.template_name] > 1
      ? `${s.template_name} (${nameSeq[s.template_name]})`
      : s.template_name

    return {
      key,
      template_id: s.template_id,   // ← stored so handleChartClick can pass to /requests
      name: displayName,
      color: palette[i % palette.length],
      counts: s.counts,
      total: s.total ?? s.counts.reduce((a, b) => a + b, 0),
    }
  })
}

// When no filter is active, collapse series beyond the top MAX_CHART_SERIES into
// a single "Other (N)" bucket so the legend never exceeds one row.
function applySeriesCap(allConfig, otherColor) {
  if (allConfig.length <= MAX_CHART_SERIES) return allConfig
  const top = allConfig.slice(0, MAX_CHART_SERIES)
  const rest = allConfig.slice(MAX_CHART_SERIES)
  const otherCounts = (top[0]?.counts ?? []).map((_, dayIdx) =>
    rest.reduce((sum, s) => sum + (s.counts[dayIdx] ?? 0), 0)
  )
  return [
    ...top,
    {
      key: '__other__',
      template_id: null,
      name: `Other (${rest.length})`,
      color: otherColor,
      counts: otherCounts,
      total: otherCounts.reduce((a, b) => a + b, 0),
    },
  ]
}

// Custom tooltip - shows only the series present in the tooltip payload
// (hidden series are not rendered so they never appear here)
function TrendTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null

  // ── Zero-value pollution fix ──────────────────────────────────────
  // In a stacked chart every series has a row for every date, even dates
  // where it had no activity. Without filtering, the tooltip shows 20+
  // entries with "0" - called "tooltip noise" or "zero-value pollution".
  // We only show series that actually had activity on this date.
  const activeItems = payload.filter(p => (p.value || 0) > 0)

  // All zero on this day → show a compact "no activity" card instead of
  // a blank or cluttered tooltip
  if (activeItems.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg px-4 py-2.5 min-w-[140px]">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">{label}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">No activity</p>
      </div>
    )
  }

  const total = activeItems.reduce((s, p) => s + (p.value || 0), 0)
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg px-4 py-3 min-w-[160px]">
      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">{label}</p>
      {activeItems.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4 text-xs mb-1">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: p.color }} />
            <span className="text-gray-700 dark:text-gray-300 truncate max-w-[120px]">{p.name}</span>
          </span>
          <span className="font-semibold text-gray-900 dark:text-gray-100">{p.value}</span>
        </div>
      ))}
      {activeItems.length > 1 && (
        <div className="border-t border-gray-100 dark:border-gray-800 mt-2 pt-1.5 flex justify-between text-xs">
          <span className="text-gray-500 dark:text-gray-400">Total</span>
          <span className="font-bold text-gray-900 dark:text-gray-100">{total}</span>
        </div>
      )}
    </div>
  )
}

// Custom legend - shows top items + overflow badge. Capped to avoid 3-row wrapping.
// Passed as a content *function* so Recharts cannot clone/override the payload.
const MAX_LEGEND_ITEMS = 8

function TrendLegend({ payload, hidden, onToggle }) {
  if (!payload || payload.length === 0) return null
  const displayed = payload.slice(0, MAX_LEGEND_ITEMS)
  const overflowCount = payload.length - displayed.length
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5 justify-center mt-4 px-1">
      {displayed.map((entry) => {
        const isHidden = hidden.includes(entry.dataKey)
        return (
          <button
            key={entry.dataKey}
            onClick={() => onToggle(entry.dataKey)}
            title={isHidden ? `Show ${entry.value}` : `Hide ${entry.value}`}
            style={{ opacity: isHidden ? 0.35 : 1 }}
            className="flex items-center gap-1.5 text-[11px] font-medium transition-opacity hover:opacity-70 group"
          >
            <span
              className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ring-2 ring-white dark:ring-gray-900"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-600 dark:text-gray-400 max-w-[140px] truncate leading-tight group-hover:text-gray-900 dark:group-hover:text-gray-200 transition-colors">{entry.value}</span>
          </button>
        )
      })}
      {overflowCount > 0 && (
        <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium self-center ml-1">
          +{overflowCount} more
        </span>
      )}
    </div>
  )
}

const RANGE_OPTIONS = [
  { label: '7D',  value: 7  },
  { label: '14D', value: 14 },
  { label: '30D', value: 30 },
]

function ProcessingTrendChart({ fullPage = false, onPointSelect = null, initialDays = 7 }) {
  const navigate = useNavigate()
  const { isDark } = useTheme()
  const chart = getChartTheme(isDark)
  const [trendDays, setTrendDays] = useState(initialDays)
  const [trendData, setTrendData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [hidden, setHidden] = useState([])
  const [selectedKeys, setSelectedKeys] = useState(null) // null = all selected
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [templateSearch, setTemplateSearch] = useState('')
  const [customOpen, setCustomOpen] = useState(false)
  const [customInputFrom, setCustomInputFrom] = useState('')
  const [customInputTo,   setCustomInputTo]   = useState('')
  const [customRange, setCustomRange] = useState(null) // { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' } | null
  const dropdownRef = useRef(null)
  const customRef = useRef(null)
  // Ref flag: prevents handleChartClick from firing after handleDotClick on the same click.
  // Recharts fires activeDot.onClick and AreaChart.onClick independently (not DOM bubbling).
  const dotClickedRef = useRef(false)

  // Fetch trend data whenever trendDays or customRange changes
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const call = customRange
      ? dashboardApi.getTrend(7, customRange.from, customRange.to)
      : dashboardApi.getTrend(trendDays)
    call
      .then(res => {
        if (!cancelled) {
          setTrendData(res.data)
          setLoading(false)
          setHidden([])
          setSelectedKeys(null)
        }
      })
      .catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [trendDays, customRange])

  // Close dropdown on outside click + Escape to close expanded
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false)
        setTemplateSearch('')
      }
      if (customRef.current && !customRef.current.contains(e.target)) {
        // Don't close if user clicked the Custom button itself (it toggles)
        setCustomOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    const escHandler = (e) => { if (e.key === 'Escape') { setDropdownOpen(false); setTemplateSearch(''); setCustomOpen(false) } }
    document.addEventListener('keydown', escHandler)
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('keydown', escHandler) }
  }, [])

  const allSeriesConfig = useMemo(() => {
    if (!trendData?.series?.length) return []
    return buildSeriesConfig(trendData.series, chart.series)
  }, [trendData, chart.series])

  // Apply template multi-select filter, then cap to MAX_CHART_SERIES + Other.
  // When the user has picked specific templates we skip the cap so their exact
  // selection shows unmodified.
  const seriesConfig = useMemo(() => {
    if (selectedKeys) {
      // User has a specific selection - show exactly those, no cap
      return allSeriesConfig.filter(s => selectedKeys.has(s.key))
    }
    // Default view: top MAX_CHART_SERIES + "Other(N)" bucket
    return applySeriesCap(allSeriesConfig, chart.neutral)
  }, [allSeriesConfig, selectedKeys, chart.neutral])

  const toggleSeries = (key) =>
    setHidden(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])

  // Toggle a single template in the dropdown. Does NOT auto-collapse back to
  // "all" when every box is checked - user must explicitly click "All templates".
  const toggleTemplate = (key) => {
    setSelectedKeys(prev => {
      const all = new Set(allSeriesConfig.map(s => s.key))
      const base = prev ?? new Set(all)
      const next = new Set(base)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      // If all templates are now selected, go back to the "all" state
      if (next.size === all.size) return null
      return next
    })
  }

  // "All templates" button - true toggle:
  //  • If currently all selected (null) → deselect all (empty Set) so user can pick individually
  //  • If currently some/none selected  → select all (null)
  const toggleAllTemplates = () => {
    if (selectedKeys === null) {
      // Deselect everything - user wants to pick specific templates
      setSelectedKeys(new Set())
    } else {
      // Select all
      setSelectedKeys(null)
    }
  }

  // Resolve a date label to ISO date string using the trendData dates array.
  // For preset ranges the end date is UTC today; for custom ranges it is customRange.to.
  const resolveDateIso = useCallback((dateLabel) => {
    if (!trendData?.dates) return ''
    const dateIdx = trendData.dates.indexOf(dateLabel)
    if (dateIdx < 0) return ''
    const endDateStr = customRange ? customRange.to : new Date().toISOString().slice(0, 10)
    const d = new Date(endDateStr) // parsed as UTC midnight
    const offset = trendData.dates.length - 1 - dateIdx
    d.setUTCDate(d.getUTCDate() - offset)
    return d.toISOString().slice(0, 10)
  }, [trendData, customRange])

  // Fallback: fires when user clicks anywhere on the chart canvas (not just a dot).
  // activePayload is only populated if the cursor was already hovering (tooltip active).
  // activeDot onClick (handleDotClick below) is the primary handler for dot clicks.
  const handleChartClick = (chartState) => {
    // If handleDotClick already handled this click, skip.
    // Recharts fires both independently (not DOM bubbling), so we use a ref flag.
    if (dotClickedRef.current) {
      dotClickedRef.current = false
      return
    }
    if (!chartState) return
    const payload = chartState.activePayload
    if (!payload?.length) return
    const dateLabel = chartState.activeLabel
    if (!dateLabel) return

    // Pick the highest-value visible series at this x position
    const topPayload = [...payload]
      .filter(p => p.value > 0)
      .sort((a, b) => b.value - a.value)[0]
    const serKey = topPayload?.dataKey
    const matchedSeries = seriesConfig.find(s => s.key === serKey)
    const dateIso = resolveDateIso(dateLabel)

    const pointData = {
      templateId: matchedSeries?.template_id ?? null,
      templateName: matchedSeries?.name || 'All Templates',
      dateIso,
      dateLabel,
    }

    if (onPointSelect) {
      onPointSelect(pointData)
    } else {
      const params = new URLSearchParams()
      if (pointData.templateId != null) {
        params.set('template_id', String(pointData.templateId))
        params.set('template_name', pointData.templateName)
      }
      if (pointData.dateIso) {
        params.set('date_iso', pointData.dateIso)
        params.set('date_label', pointData.dateLabel)
      }
      params.set('days', String(trendDays))
      navigate(`/dashboard/trend?${params.toString()}`)
    }
  }

  // Dot-level click handler - attached to each Area's activeDot.
  // Recharts 2.x calls onClick(dotProps, event); Recharts 3.x calls onClick(event, dotProps).
  // We inspect both args and take whichever one has a dataKey so we work with either version.
  const handleDotClick = useCallback((firstArg, secondArg) => {
    const isDotProps = (a) => a != null && typeof a === 'object' && 'dataKey' in a
    const dotProps = isDotProps(firstArg) ? firstArg : isDotProps(secondArg) ? secondArg : null
    if (!dotProps) return

    // Flag to prevent handleChartClick from also firing (Recharts fires both independently)
    dotClickedRef.current = true

    const serKey    = dotProps.dataKey
    const row       = dotProps.payload   // full chart row: { date: '5 Apr', t123: 3, ... }
    const dateLabel = row?.date
    if (!serKey || !dateLabel) return

    // FIX: In a stacked chart, activeDots render for ALL series at the hovered x.
    // The topmost SVG area (often low-volume/zero-value series) captures the click.
    // If the clicked series has 0 at this date, find the highest-value series instead.
    let resolvedKey = serKey
    const clickedValue = row[serKey] ?? 0
    if (clickedValue === 0) {
      // Find the series with the highest value at this date
      const best = seriesConfig
        .filter(s => (row[s.key] ?? 0) > 0)
        .sort((a, b) => (row[b.key] ?? 0) - (row[a.key] ?? 0))[0]
      if (!best) return  // all zero on this date, nothing to drill into
      resolvedKey = best.key
    }

    const matchedSeries = seriesConfig.find(s => s.key === resolvedKey)
    const dateIso = resolveDateIso(dateLabel)

    const pointData = {
      templateId: matchedSeries?.template_id ?? null,
      templateName: matchedSeries?.name || 'All Templates',
      dateIso,
      dateLabel,
    }

    if (onPointSelect) {
      onPointSelect(pointData)
    } else {
      const params = new URLSearchParams()
      if (pointData.templateId != null) {
        params.set('template_id', String(pointData.templateId))
        params.set('template_name', pointData.templateName)
      }
      if (pointData.dateIso) {
        params.set('date_iso', pointData.dateIso)
        params.set('date_label', pointData.dateLabel)
      }
      params.set('days', String(trendDays))
      navigate(`/dashboard/trend?${params.toString()}`)
    }
  }, [seriesConfig, trendData, navigate, onPointSelect, resolveDateIso, trendDays])

  // Build chart data rows
  const chartData = useMemo(() => {
    if (!trendData?.dates || !seriesConfig.length) return []
    return trendData.dates.map((date, idx) => {
      const row = { date }
      seriesConfig.forEach(s => { row[s.key] = s.counts[idx] ?? 0 })
      return row
    })
  }, [trendData, seriesConfig])

  const visibleConfig = seriesConfig.filter(s => !hidden.includes(s.key))
  const legendPayload = seriesConfig.map(s => ({ dataKey: s.key, value: s.name, color: s.color }))
  const totalTemplates = allSeriesConfig.length
  const selectedCount = selectedKeys ? selectedKeys.size : totalTemplates

  // Effective days: use custom range span when active, otherwise preset trendDays
  const effectiveDays = customRange
    ? Math.max(1, Math.round((new Date(customRange.to) - new Date(customRange.from)) / 86400000) + 1)
    : trendDays
  // Monthly view: more than 2 months of data
  const isMonthlyView = effectiveDays > 60

  // Base interval on actual returned data points, not requested trendDays,
  // so the spacing is always correct regardless of backend caps.
  const numPoints = chartData.length
  const xAxisInterval = isMonthlyView
    ? 0
    : numPoints <= 7
      ? 0
      : numPoints <= 14
        ? 1
        : Math.max(0, Math.ceil(numPoints / 7) - 1)

  // Monthly view should show one label per month-year to avoid repeated month names.
  // Returns both an array (for the ticks prop) and a Map (for fast value-based lookup).
  const { monthlyTicks, monthlyTickMap } = useMemo(() => {
    if (!isMonthlyView || chartData.length === 0) return { monthlyTicks: [], monthlyTickMap: new Map() }
    const endDateStr = customRange ? customRange.to : new Date().toISOString().slice(0, 10)
    const utcEnd = new Date(endDateStr)
    const ticks = []
    const tickMap = new Map()
    const seenMonthYears = new Set()

    chartData.forEach((row, idx) => {
      const offset = chartData.length - 1 - idx
      const d = new Date(utcEnd)
      d.setUTCDate(d.getUTCDate() - offset)

      const monthYearKey = `${d.getUTCFullYear()}-${d.getUTCMonth()}`
      if (seenMonthYears.has(monthYearKey)) return
      seenMonthYears.add(monthYearKey)

      const month = d.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' })
      const year = String(d.getUTCFullYear()).slice(-2)
      const text = `${month} '${year}`
      ticks.push({ value: row.date, text })
      tickMap.set(row.date, text)
    })

    return { monthlyTicks: ticks, monthlyTickMap: tickMap }
  }, [chartData, isMonthlyView, customRange])

  // For monthly views format "5 Apr" → "Apr '26"; daily keeps full "5 Apr"
  const xAxisTickFormatter = (label) => {
    if (!label || !isMonthlyView) return label
    return label
  }

  if (loading) {
    return (
      <div aria-busy="true" aria-label="Loading" className="skeleton h-72 w-full rounded-lg" />
    )
  }

  const hasData = chartData.length > 0 && visibleConfig.length > 0

  // Chart container height - tall on the dedicated full-page view, compact on dashboard
  const chartHeight = fullPage ? 'h-[55vh]' : 'h-72'

  const chartContent = (
    <div>
      {/* Unified header: title + range pills + custom picker + expand + template dropdown */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Link to="/requests" className="text-base font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap hover:text-navy-600 dark:hover:text-navy-400 transition-colors">Processing Trend</Link>
          {/* Preset range pills */}
          <div className="flex items-center gap-0.5 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
            {RANGE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => { setTrendDays(opt.value); setCustomRange(null); setCustomOpen(false) }}
                className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                  trendDays === opt.value && !customOpen && !customRange
                    ? 'bg-white dark:bg-gray-700 text-navy-700 dark:text-navy-300 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
            {/* Custom range button */}
            <button
              onClick={() => setCustomOpen(o => !o)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                (customOpen || customRange)
                  ? 'bg-white dark:bg-gray-700 text-navy-700 dark:text-navy-300 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              Custom
            </button>
          </div>
          {/* Custom date range picker - matches the global dashboard filter style */}
          {customOpen && (
            <div ref={customRef} className="flex items-center gap-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg px-3 py-2">
              <DatePicker value={customInputFrom} onChange={setCustomInputFrom} max={customInputTo || new Date().toISOString().slice(0, 10)} size="sm" placeholder="From" ariaLabel="From date" />
              <span className="text-xs text-gray-400 dark:text-gray-500">to</span>
              <DatePicker value={customInputTo} onChange={setCustomInputTo} min={customInputFrom || undefined} max={new Date().toISOString().slice(0, 10)} size="sm" placeholder="To" ariaLabel="To date" />
              <button
                onClick={() => {
                  if (!customInputFrom || !customInputTo) return
                  if (customInputFrom > customInputTo) return
                  setCustomRange({ from: customInputFrom, to: customInputTo })
                  setCustomOpen(false)
                }}
                disabled={!customInputFrom || !customInputTo || customInputFrom > customInputTo}
                className="inline-flex items-center px-3 py-1.5 text-xs font-semibold bg-navy-800 hover:bg-navy-900 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                Apply
              </button>
              {customRange && (
                <button
                  onClick={() => { setCustomRange(null); setCustomInputFrom(''); setCustomInputTo('') }}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors rounded"
                  title="Clear custom range"
                >
                  <RefreshCw className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={() => setCustomOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors rounded"
                title="Cancel"
              >
                ✕
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Expand button - only shown on the dashboard, not on the full trend page */}
          {!fullPage && (
            <button
              onClick={() => navigate(`/dashboard/trend?days=${trendDays}`)}
              title="View full trend page"
              className="p-1.5 rounded-md text-gray-400 dark:text-gray-500 hover:text-navy-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          )}

          {totalTemplates >= 1 && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(o => !o)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-navy-400 hover:text-navy-700 dark:hover:text-navy-300 transition-colors"
              >
                <span>
                  {selectedKeys === null
                    ? `All templates (${totalTemplates})`
                    : `${selectedCount} / ${totalTemplates} templates`}
              </span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl min-w-[240px] max-h-72 flex flex-col py-1">
                {/* Search box - shown when there are >6 templates */}
                {allSeriesConfig.length > 6 && (
                  <div className="px-2 pb-1 pt-0.5 flex-shrink-0">
                    <input
                      autoFocus
                      type="text"
                      value={templateSearch}
                      onChange={e => setTemplateSearch(e.target.value)}
                      placeholder="Search templates…"
                      className="w-full px-2 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 placeholder-gray-400 focus:outline-none focus:border-navy-400"
                    />
                  </div>
                )}
                <div className="overflow-y-auto flex-1">
                  {!templateSearch && (
                    <>
                      <button
                        onClick={() => { toggleAllTemplates(); setTemplateSearch('') }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-navy-700 dark:text-navy-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        {selectedKeys === null
                          ? <CheckSquare className="w-3.5 h-3.5 flex-shrink-0" />
                          : <Square className="w-3.5 h-3.5 flex-shrink-0" />}
                        <span>All templates</span>
                        <span className="ml-auto text-gray-400 font-normal">{totalTemplates}</span>
                      </button>
                      <div className="border-t border-gray-100 dark:border-gray-800 my-0.5" />
                    </>
                  )}
                  {allSeriesConfig
                    .filter(s => !templateSearch || s.name.toLowerCase().includes(templateSearch.toLowerCase()))
                    .map(s => {
                      const checked = selectedKeys === null || selectedKeys.has(s.key)
                      return (
                        <button
                          key={s.key}
                          onClick={() => toggleTemplate(s.key)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                        >
                          {checked
                            ? <CheckSquare className="w-3.5 h-3.5 flex-shrink-0" style={{ color: s.color }} />
                            : <Square className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />}
                          <span className="truncate flex-1 text-left">{s.name}</span>
                          <span className="flex-shrink-0 text-gray-400 tabular-nums">{s.total ?? 0}</span>
                        </button>
                      )
                    })}
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Chart area */}
      {!hasData ? (
        <div className={`${chartHeight} flex flex-col items-center justify-center gap-2 text-gray-400`}>
          <BarChart3 className="w-8 h-8 opacity-30" />
          <p className="text-sm">
            {selectedKeys && selectedKeys.size === 0
              ? 'Select a template from the dropdown above'
              : 'No data for this period'}
          </p>
        </div>
      ) : (
        <div className={chartHeight}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 8, right: 20, left: -12, bottom: 0 }}
              onClick={handleChartClick}
              style={{ cursor: 'pointer' }}
            >
              <defs>
                {seriesConfig.map(s => (
                  <linearGradient key={s.key} id={`grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor={s.color} stopOpacity={0.12} />
                    <stop offset="100%" stopColor={s.color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: chart.axis }}
                tickLine={false}
                axisLine={false}
                interval={xAxisInterval}
                ticks={isMonthlyView ? monthlyTicks.map(t => t.value) : undefined}
                tickFormatter={(label) => isMonthlyView
                  ? (monthlyTickMap.get(label) || '')
                  : xAxisTickFormatter(label)
                }
                angle={0}
                textAnchor="middle"
                height={32}
                padding={{ left: 8, right: 12 }}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: chart.axis }}
                tickLine={false}
                axisLine={false}
                width={36}
                domain={[0, (dataMax) => Math.max(dataMax + 2, 5)]}
              />
              <Tooltip content={<TrendTooltip />} />
              <Legend
                content={() => (
                  <TrendLegend
                    payload={legendPayload}
                    hidden={hidden}
                    onToggle={toggleSeries}
                  />
                )}
              />
              {visibleConfig.map(s => (
                <Area
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.name}
                  stroke={s.color}
                  strokeWidth={2}
                  fill={`url(#grad-${s.key})`}
                  fillOpacity={0.6}
                  dot={false}
                  activeDot={{
                    r: 6,
                    strokeWidth: 2,
                    stroke: '#fff',
                    style: { cursor: 'pointer' },
                    onClick: handleDotClick,
                  }}
                  animationDuration={1500}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )

  return chartContent
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-page: /dashboard/trend
// Combined view: Processing Trend chart at top + inline requests drilldown below.
// Clicking a data point on the chart shows the matching requests inline.
// Supports initial selection via URL params (template_id, date_iso, etc.)
// ─────────────────────────────────────────────────────────────────────────────
export function DashboardTrendFull() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Read the days param so the chart starts with the same range the user had on dashboard
  const initialDays = parseInt(searchParams.get('days'), 10) || 7

  // Selected point state - drives the inline requests panel
  const [selectedPoint, setSelectedPoint] = useState(() => {
    const templateId = searchParams.get('template_id')
    const templateName = searchParams.get('template_name') || 'All Templates'
    const dateIso = searchParams.get('date_iso') || ''
    const dateLabel = searchParams.get('date_label') || dateIso
    if (templateId || dateIso) {
      return { templateId: templateId ? Number(templateId) : null, templateName, dateIso, dateLabel }
    }
    return null
  })

  // Requests data for the selected point
  const [items, setItems] = useState([])
  const [drillLoading, setDrillLoading] = useState(false)
  const [drillError, setDrillError] = useState(null)

  // Handle point selection from chart
  const handlePointSelect = useCallback((pointData) => {
    setSelectedPoint(pointData)
    setViewAllMode(false) // reset to date-scoped view when a new point is picked
    // Update URL params so the selection is shareable / survives refresh
    const params = new URLSearchParams()
    if (pointData.templateId != null) {
      params.set('template_id', String(pointData.templateId))
      params.set('template_name', pointData.templateName)
    }
    if (pointData.dateIso) {
      params.set('date_iso', pointData.dateIso)
      params.set('date_label', pointData.dateLabel)
    }
    setSearchParams(params, { replace: true })
  }, [setSearchParams])

  // When true, the inline table shows ALL requests for the template (no date filter)
  const [viewAllMode, setViewAllMode] = useState(false)

  // Clear selection
  const handleClearSelection = () => {
    setSelectedPoint(null)
    setViewAllMode(false)
    setSearchParams({}, { replace: true })
    setItems([])
  }

  // Expand to all requests for the selected template, inline (no navigation)
  const handleViewAll = () => {
    setViewAllMode(true)
  }

  // Fetch requests whenever selectedPoint or viewAllMode changes
  useEffect(() => {
    if (!selectedPoint) { setItems([]); return }
    let cancelled = false
    setDrillLoading(true)
    setDrillError(null)
    const params = { per_page: 500 }
    if (selectedPoint.templateId != null) params.template_id = selectedPoint.templateId
    // Only apply date filter when NOT in view-all mode
    if (!viewAllMode && selectedPoint.dateIso) { params.date_from = selectedPoint.dateIso; params.date_to = selectedPoint.dateIso }
    requestsApi.list(params)
      .then(res => { if (!cancelled) { setItems(res.data?.requests ?? []); setDrillLoading(false) } })
      .catch(() => { if (!cancelled) { setDrillError('Failed to load requests.'); setDrillLoading(false) } })
    return () => { cancelled = true }
  }, [selectedPoint, viewAllMode])

  const statusCfg = {
    pending:    { bg: 'bg-yellow-50 dark:bg-yellow-900/20', text: 'text-yellow-700 dark:text-yellow-300', dot: 'bg-yellow-500',  label: 'Pending'    },
    processing: { bg: 'bg-blue-50 dark:bg-blue-900/20',    text: 'text-blue-700 dark:text-blue-300',    dot: 'bg-blue-500',    label: 'Processing' },
    reviewing:  { bg: 'bg-orange-50 dark:bg-orange-900/20',text: 'text-orange-700 dark:text-orange-300',dot: 'bg-orange-500',  label: 'Reviewing'  },
    completed:  { bg: 'bg-green-50 dark:bg-green-900/20',  text: 'text-green-700 dark:text-green-300',  dot: 'bg-green-500',   label: 'Completed'  },
    cancelled:  { bg: 'bg-gray-100 dark:bg-gray-800',      text: 'text-gray-600 dark:text-gray-400',    dot: 'bg-gray-400',    label: 'Cancelled'  },
    failed:     { bg: 'bg-red-50 dark:bg-red-900/20',      text: 'text-red-700 dark:text-red-300',      dot: 'bg-red-500',     label: 'Failed'     },
    extracted:  { bg: 'bg-indigo-50 dark:bg-indigo-900/20',text: 'text-indigo-700 dark:text-indigo-300',dot: 'bg-indigo-500',  label: 'Extracted'  },
    approved:   { bg: 'bg-green-50 dark:bg-green-900/20',  text: 'text-green-700 dark:text-green-300',  dot: 'bg-green-500',   label: 'Approved'   },
    rejected:   { bg: 'bg-red-50 dark:bg-red-900/20',      text: 'text-red-700 dark:text-red-300',      dot: 'bg-red-500',     label: 'Rejected'   },
  }
  const getStatus = (s) => statusCfg[s] ?? { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400', label: s }



  return (
    <div className="space-y-5">
      {/* Back navigation */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-navy-600 dark:hover:text-navy-400 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Dashboard
        </button>
      </div>

      {/* Chart card */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
        <ProcessingTrendChart fullPage onPointSelect={handlePointSelect} initialDays={initialDays} />
      </div>

      {/* Inline drilldown - only shown when a point is selected */}
      {selectedPoint && (
        <>
          {/* Context banner */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 px-5 py-4">
            <div className="flex flex-wrap items-center gap-5">
              <div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Template</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{selectedPoint.templateName}</p>
              </div>
              <div className="h-8 w-px bg-gray-200 dark:bg-gray-700" />
              <div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Date</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{selectedPoint.dateLabel || '-'}</p>
              </div>
              <div className="h-8 w-px bg-gray-200 dark:bg-gray-700" />
              <div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Requests</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {drillLoading ? '…' : items.length}{viewAllMode ? '' : ` on ${selectedPoint.dateLabel}`}
                </p>
              </div>
              <div className="ml-auto flex items-center gap-3">
                {!viewAllMode && (
                  <button
                    onClick={handleViewAll}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-navy-600 dark:text-navy-400 hover:text-navy-800 dark:hover:text-navy-300 transition-colors"
                  >
                    View all requests for this template
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </button>
                )}
                {viewAllMode && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-600 dark:text-green-400">
                    Showing all requests for this template
                  </span>
                )}
                <button
                  onClick={handleClearSelection}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <RefreshCw className="h-3 w-3" />
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* Requests table */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
            {drillLoading && (
              <ListRowsSkeleton rows={8} />
            )}

            {!drillLoading && drillError && (
              <div className="py-16 flex flex-col items-center gap-3">
                <AlertCircle className="h-8 w-8 text-red-400" />
                <p className="text-sm text-gray-500 dark:text-gray-400">{drillError}</p>
              </div>
            )}

            {!drillLoading && !drillError && items.length === 0 && (
              <div className="py-20 flex flex-col items-center gap-3 text-gray-400">
                <FileText className="h-10 w-10 opacity-25" />
                <p className="text-sm">No requests found for this selection</p>
              </div>
            )}

            {!drillLoading && !drillError && items.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-800/60 border-b border-gray-100 dark:border-gray-800">
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Request</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">AI Confidence</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Submitted By</th>
                      <th className="px-2 py-3 w-10" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {items.map(req => {
                      const sc = getStatus(req.status)
                      const conf = req.ai_confidence != null
                        ? Math.min(100, Math.max(0, Math.round(req.ai_confidence)))
                        : null
                      const confBar = conf == null ? null
                        : conf >= 90 ? 'bg-green-500'
                        : conf >= 70 ? 'bg-yellow-500'
                        : 'bg-red-500'
                      const submitter = req.created_by_name || req.submitted_by || 'Unknown'
                      const initials = submitter.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                      const title = req.title || `Request #${String(req.id).slice(0, 8)}`

                      return (
                        <tr key={req.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-navy-50 dark:bg-navy-900/30 flex items-center justify-center flex-shrink-0">
                                <FileText className="h-4 w-4 text-navy-600 dark:text-navy-400" />
                              </div>
                              <Link
                                to={`/requests/${req.id}`}
                                className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-navy-600 dark:hover:text-navy-400 transition-colors line-clamp-1"
                              >
                                {title}
                              </Link>
                            </div>
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap">
                            <p className="text-sm text-gray-900 dark:text-gray-100">{formatDate(req.created_at)}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">{formatTime(req.created_at)}</p>
                          </td>
                          <td className="px-5 py-3">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sc.bg} ${sc.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sc.dot}`} />
                              {sc.label}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            {conf != null ? (
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${confBar}`} style={{ width: `${conf}%` }} />
                                </div>
                                <span className="text-sm text-gray-600 dark:text-gray-400 tabular-nums">{conf}%</span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-600 dark:text-gray-300 flex-shrink-0">
                                {initials}
                              </div>
                              <span className="text-sm text-gray-600 dark:text-gray-400 line-clamp-1">{submitter}</span>
                            </div>
                          </td>
                          <td className="px-2 py-3">
                            <Link
                              to={`/requests/${req.id}`}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-navy-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors inline-flex"
                              title="Open request"
                            >
                              <Eye className="h-4 w-4" />
                            </Link>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Hint when no point selected */}
      {!selectedPoint && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 px-6 py-12 text-center">
          <BarChart3 className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Click a data point on the chart above to see the matching requests</p>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-page: /dashboard/drilldown (LEGACY - redirects to /dashboard/trend)
// Kept for backwards compatibility with bookmarks / shared links.
// ─────────────────────────────────────────────────────────────────────────────
export function DashboardDrillDown() {
  const [searchParams] = useSearchParams()
  // Forward all existing query params to the trend page
  const params = searchParams.toString()
  return <Navigate to={`/dashboard/trend${params ? `?${params}` : ''}`} replace />
}

// Status Donut Chart Component
function StatusDonutChart({ data, total }) {
  const { isDark } = useTheme()
  const chart = getChartTheme(isDark)
  const [animProgress, setAnimProgress] = useState(0)
  const animRef = useRef(null)
  const dataKey = JSON.stringify(data)

  // Animate segments from 0 to full on mount or data change
  useEffect(() => {
    setAnimProgress(0)
    const startTime = performance.now()
    const duration = 1500 // ms - slow and smooth

    const easeInOutCubic = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

    const animate = (now) => {
      const elapsed = now - startTime
      const t = Math.min(elapsed / duration, 1)
      setAnimProgress(easeInOutCubic(t))
      if (t < 1) {
        animRef.current = requestAnimationFrame(animate)
      }
    }
    animRef.current = requestAnimationFrame(animate)
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current) }
  }, [dataKey])

  const colors = chart.status
  
  const labels = {
    completed: 'Completed',
    reviewing: 'Reviewing',
    processing: 'Processing',
    pending: 'Pending',
    cancelled: 'Cancelled',
  }
  
  // Use safe total to avoid NaN
  const safeTotal = total || 1
  const allZero = Object.values(data).every(v => v === 0)
  
  // Gap between segments in degrees
  const gapDeg = 3
  
  // Calculate percentages and angles - filter out zero-value segments
  // Apply animProgress to scale all angles from 0 to target
  let currentAngle = 0
  const segments = Object.entries(data)
    .map(([key, value]) => {
      const percentage = (value / safeTotal) * 100
      const startAngle = currentAngle * animProgress
      const fullEnd = currentAngle + (percentage / 100) * 360
      currentAngle = fullEnd
      return { key, value, percentage, startAngle, endAngle: fullEnd * animProgress, color: colors[key] }
    })
  
  const nonZeroSegments = segments.filter(s => s.value > 0)
  
  // Create SVG path for each segment
  const createArcPath = (startAngle, endAngle, innerRadius, outerRadius) => {
    // SVG arcs cannot draw a full 360° circle, so cap at 359.99°
    const clampedEnd = Math.min(endAngle, startAngle + 359.99)
    if (clampedEnd - startAngle < 0.1) return '' // too small to draw
    const startOuter = polarToCartesian(100, 100, outerRadius, clampedEnd)
    const endOuter = polarToCartesian(100, 100, outerRadius, startAngle)
    const startInner = polarToCartesian(100, 100, innerRadius, clampedEnd)
    const endInner = polarToCartesian(100, 100, innerRadius, startAngle)
    const largeArcFlag = clampedEnd - startAngle <= 180 ? 0 : 1
    
    return [
      'M', startOuter.x, startOuter.y,
      'A', outerRadius, outerRadius, 0, largeArcFlag, 0, endOuter.x, endOuter.y,
      'L', endInner.x, endInner.y,
      'A', innerRadius, innerRadius, 0, largeArcFlag, 1, startInner.x, startInner.y,
      'Z'
    ].join(' ')
  }
  
  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians)
    }
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 w-full">
      <div className="relative w-[180px] h-[180px] sm:w-[200px] sm:h-[200px] shrink-0">
        <svg viewBox="0 0 200 200" className="w-full h-full transform -rotate-90">
          {allZero ? (
            /* Empty state: full gray ring */
            <circle cx="100" cy="100" r="72.5" fill="none" stroke={chart.track} strokeWidth="25" />
          ) : nonZeroSegments.length === 1 ? (
            /* Single segment: animated ring */
            <path
              d={createArcPath(0, 359.99 * animProgress, 60, 85)}
              fill={nonZeroSegments[0].color}
            />
          ) : (
            /* Multiple segments with gaps - animated */
            nonZeroSegments.map(seg => {
              const spanDeg = seg.endAngle - seg.startAngle
              if (spanDeg < 0.1) return null
              const halfGap = gapDeg / 2
              // Only apply gap if segment is wide enough
              const start = spanDeg > gapDeg * 2 ? seg.startAngle + halfGap : seg.startAngle
              const end = spanDeg > gapDeg * 2 ? seg.endAngle - halfGap : seg.endAngle
              const path = createArcPath(start, end, 60, 85)
              if (!path) return null
              return (
                <path
                  key={seg.key}
                  d={path}
                  fill={seg.color}
                  rx="4"
                />
              )
            })
          )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{total || 0}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-1 gap-2 sm:gap-3">
        {segments.map(seg => (
          <div key={seg.key} className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-sm shrink-0" style={{ backgroundColor: seg.color }} />
            <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">{labels[seg.key]}</span>
            <span className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100">{seg.value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// Document Row Component
function DocumentRow({ request, index }) {
  const confidence = request.ai_confidence != null ? Math.min(100, Math.max(0, request.ai_confidence)) : 0
  
  // Get created by info
  const createdByName = request.created_by_name || 'Unknown'
  const createdByInitials = createdByName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'UN'
  
  // Get documents info
  const documents = request.documents || []
  const documentCount = documents.length || request.document_count || 0
  const documentNames = documents.length > 0
    ? documents.map(d => d.filename || d.name || 'Unknown').join('\n')
    : (request.document_names || []).join('\n')
  
  // Truncate description for display
  const description = request.description || ''
  const maxDescLength = 50
  const truncatedDesc = description.length > maxDescLength 
    ? description.substring(0, maxDescLength) + '...' 
    : description
  const showTooltip = description.length > maxDescLength
  
  const getStatusBadge = (status) => {
    const config = {
      pending: { bg: 'bg-yellow-50 dark:bg-yellow-900/20', text: 'text-yellow-700 dark:text-yellow-300', dot: 'bg-yellow-500', label: 'Pending' },
      processing: { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-300', dot: 'bg-blue-500', label: 'Processing' },
      extracted: { bg: 'bg-indigo-50 dark:bg-indigo-900/20', text: 'text-indigo-600 dark:text-indigo-300', dot: 'bg-indigo-500', label: 'Extracted' },
      reviewing: { bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-600 dark:text-orange-300', dot: 'bg-orange-500', label: 'Reviewing' },
      approved: { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-600 dark:text-green-300', dot: 'bg-green-500', label: 'Approved' },
      rejected: { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-600 dark:text-red-300', dot: 'bg-red-500', label: 'Rejected' },
      failed: { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-600 dark:text-red-300', dot: 'bg-red-500', label: 'Failed' },
      completed: { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-600 dark:text-green-300', dot: 'bg-green-500', label: 'Completed' },
      cancelled: { bg: 'bg-gray-50 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-300', dot: 'bg-gray-500', label: 'Cancelled' },
    }
    return config[status] || config.pending
  }
  
  const statusConfig = getStatusBadge(request.status)
  const confidenceColor = confidence >= 90 ? 'bg-green-500' : confidence >= 70 ? 'bg-yellow-500' : 'bg-red-500'
  
  const getIconByStatus = (status) => {
    const colors = {
      pending: 'text-yellow-600 bg-yellow-50 dark:text-yellow-400 dark:bg-yellow-900/20',
      processing: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20',
      extracted: 'text-indigo-600 bg-indigo-50 dark:text-indigo-400 dark:bg-indigo-900/20',
      reviewing: 'text-orange-600 bg-orange-50 dark:text-orange-400 dark:bg-orange-900/20',
      approved: 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20',
      rejected: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20',
      failed: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20',
      completed: 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20',
      cancelled: 'text-gray-600 bg-gray-50 dark:text-gray-400 dark:bg-gray-700',
    }
    return colors[status] || 'text-gray-600 bg-gray-50 dark:text-gray-400 dark:bg-gray-700'
  }
  
  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div 
            className={`w-10 h-10 rounded-lg flex items-center justify-center relative cursor-default ${getIconByStatus(request.status)}`}
            title={documentCount > 0 ? documentNames : 'No documents'}
          >
            <FileText className="h-5 w-5" />
            <span className="absolute -top-1 -right-1 bg-navy-600 text-white text-[10px] font-medium rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
              {documentCount}
            </span>
          </div>
          <div className="max-w-xs">
            <Link to={`/requests/${request.id}`} className="font-medium text-gray-900 dark:text-gray-100 hover:text-navy-600 dark:hover:text-navy-400">
              {request.title || request.email_subject || `Request #${String(request.id).substring(0, 8)}`}
            </Link>
            {description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" title={showTooltip ? description : undefined}>
                {truncatedDesc || 'No description'}
              </p>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <div>
          <p className="text-sm text-gray-900 dark:text-gray-100">{formatDate(request.created_at)}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{formatTime(request.created_at)}</p>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`}></span>
          {statusConfig.label}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="w-16 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${confidenceColor}`} style={{ width: `${confidence}%` }} />
          </div>
          <span className="text-sm text-gray-600 dark:text-gray-400">{confidence}%</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300">
            {createdByInitials}
          </div>
          <span className="text-sm text-gray-600 dark:text-gray-400">{createdByName}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          {request.status === 'completed' || request.status === 'cancelled' ? (
            <Link
              to={`/requests/${request.id}?mode=view`}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="View request"
            >
              <Eye className="h-4 w-4" />
            </Link>
          ) : (
            <Link
              to={`/requests/${request.id}?mode=edit`}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Edit request"
            >
              <Edit3 className="h-4 w-4" />
            </Link>
          )}
        </div>
      </td>
    </tr>
  )
}

// Activity Item Component
function ActivityItem({ icon, iconBg, title, time }) {
  return (
    <div className="px-5 py-3 flex items-start gap-3">
      <div className={`w-8 h-8 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 dark:text-gray-100">{title}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{time}</p>
      </div>
    </div>
  )
}

// Health Metric Component
function HealthMetric({ label, value, percentage, color, showBar = true }) {
  const colorClasses = {
    green: 'bg-green-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500',
  }
  
  const textColors = {
    green: 'text-green-600',
    orange: 'text-orange-600',
    red: 'text-red-600',
  }
  
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
        <span className={`text-sm font-medium ${textColors[color]}`}>{value}</span>
      </div>
      {showBar && (
        <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full ${colorClasses[color]}`} 
            style={{ width: `${percentage}%` }} 
          />
        </div>
      )}
    </div>
  )
}

// Utility Functions (formatDate & formatTime imported from ../utils/dateUtils)

function generatePageNumbers(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  
  if (current <= 3) {
    return [1, 2, 3, '...', total]
  }
  
  if (current >= total - 2) {
    return [1, '...', total - 2, total - 1, total]
  }
  
  return [1, '...', current - 1, current, current + 1, '...', total]
}

export default Dashboard
